// apolo/src/components/plugins/SkillDocPanel.jsx — harness-library-uplift E3-WS1-T01.
//
// Painel de DOCS por skill: renderiza o playbook (`content`) que
// `GET /api/skills/:name` JÁ devolve — até aqui só o modelo lia esse conteúdo
// (achado P1 da auditoria 12/07: "nenhuma página de docs por skill").
// Markdown via MarkdownMessage (pipeline lazy + sanitizado do chat — sem HTML cru).

import { useEffect, useState } from 'react';
import { ArrowLeft, BookOpen, Loader2 } from 'lucide-react';
import { apiFetch } from '../../lib/api.js';
import MarkdownMessage from '../MarkdownMessage.jsx';

/**
 * @param {{ skill: { name: string, label?: string, description?: string } | null,
 *           onBack: () => void }} props
 */
export default function SkillDocPanel({ skill, onBack }) {
  const [state, setState] = useState({ loading: false, content: null, error: null });

  useEffect(() => {
    if (!skill?.name) return;
    let alive = true;
    setState({ loading: true, content: null, error: null });
    apiFetch(`/api/skills/${encodeURIComponent(skill.name)}`)
      .then((res) => {
        if (!alive) return;
        if (res.ok && res.data?.skill?.content) {
          setState({ loading: false, content: res.data.skill.content, error: null });
        } else if (res.ok || /not found/i.test(res.error || '')) {
          // 404 = a skill não tem playbook no store — estado honesto, não erro
          // (comandos diretos de plugin builtin vivem só no catálogo; provado no dogfood 07-14).
          setState({ loading: false, content: null, error: 'sem-conteudo' });
        } else {
          setState({ loading: false, content: null, error: res.error || 'falha ao carregar' });
        }
      })
      .catch(() => alive && setState({ loading: false, content: null, error: 'falha ao carregar' }));
    return () => { alive = false; };
  }, [skill?.name]);

  if (!skill) return null;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="skill-doc-panel">
      <div className="flex shrink-0 items-center gap-2 border-b border-apolo-borderSubtle px-4 py-2.5">
        <button
          type="button"
          onClick={onBack}
          aria-label="Voltar pro catálogo"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary transition hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
        >
          <ArrowLeft size={14} aria-hidden="true" />
        </button>
        <BookOpen size={14} aria-hidden="true" className="shrink-0 text-apolo-accent" />
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-apolo-textPrimary">{skill.label || skill.name}</h3>
          {skill.description && (
            <p className="truncate text-[11px] text-apolo-textTertiary">{skill.description}</p>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {state.loading && (
          <p className="inline-flex items-center gap-2 text-xs text-apolo-textTertiary">
            <Loader2 size={13} className="animate-spin" aria-hidden="true" /> Carregando playbook…
          </p>
        )}
        {!state.loading && state.error === 'sem-conteudo' && (
          <p className="text-xs text-apolo-textTertiary">
            Esta skill não tem playbook próprio — ela é um comando direto (a descrição acima é tudo que ela precisa).
          </p>
        )}
        {!state.loading && state.error && state.error !== 'sem-conteudo' && (
          <p className="text-xs text-apolo-error">Não deu pra carregar a doc: {state.error}. Tente de novo.</p>
        )}
        {!state.loading && state.content && (
          <div className="text-sm leading-relaxed">
            <MarkdownMessage content={state.content} />
          </div>
        )}
      </div>
    </div>
  );
}
